import type * as PdfjsNs from 'pdfjs-dist';

type PdfDoc = Awaited<ReturnType<typeof PdfjsNs.getDocument>['promise']>;
type PdfPage = Awaited<ReturnType<PdfDoc['getPage']>>;
type PageViewport = ReturnType<PdfPage['getViewport']>;
type TextContent = Awaited<ReturnType<PdfPage['getTextContent']>>;

export type PdfCacheEntry = {
  pageIndex: number;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  viewport: PageViewport;
  textContent: TextContent;
};

/**
 * LRU cache of rasterized PDF pages with their text content. Pages are
 * rendered on demand and evicted when the cache is full. We
 * deliberately cap capacity (default 5) so a 50-page PDF doesn't pin
 * ~175 MB of canvas memory and OOM mobile WebKit, which is exactly
 * what we just spent two PRs fixing.
 *
 * Cached canvases hold the raw rasterized page only — no redaction
 * rectangles. The preview blits a cached page to the visible canvas
 * and overlays redactions per render, so toggling a detection never
 * invalidates the cache.
 *
 * textContent is fetched in the same render pass and cached alongside
 * so the Phase 3 text-layer overlay doesn't have to round-trip the
 * pdfjs worker on every page change.
 */
export class PdfPageCache {
  private readonly entries = new Map<number, PdfCacheEntry>();
  private readonly inflight = new Map<number, Promise<PdfCacheEntry>>();

  constructor(
    private readonly sourceDoc: PdfDoc,
    private readonly scale: number,
    private readonly capacity = 5,
  ) {}

  async get(pageIndex: number): Promise<PdfCacheEntry> {
    const existing = this.entries.get(pageIndex);
    if (existing) {
      this.entries.delete(pageIndex);
      this.entries.set(pageIndex, existing);
      return existing;
    }
    const pending = this.inflight.get(pageIndex);
    if (pending) return pending;

    const promise = this.render(pageIndex).then((entry) => {
      this.evictIfFull();
      this.entries.set(pageIndex, entry);
      this.inflight.delete(pageIndex);
      return entry;
    });
    this.inflight.set(pageIndex, promise);
    return promise;
  }

  prefetch(pageIndex: number): void {
    if (this.entries.has(pageIndex) || this.inflight.has(pageIndex)) return;
    void this.get(pageIndex).catch(() => undefined);
  }

  dispose(): void {
    for (const e of this.entries.values()) {
      e.canvas.width = 0;
      e.canvas.height = 0;
    }
    this.entries.clear();
    this.inflight.clear();
  }

  private async render(pageIndex: number): Promise<PdfCacheEntry> {
    const page = await this.sourceDoc.getPage(pageIndex + 1);
    const viewport = page.getViewport({ scale: this.scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('PdfPageCache: 2D context unavailable');
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    // Pull text content before cleanup; cleanup() releases the native
    // operator list so a later getTextContent would have to re-parse
    // the page from scratch.
    const textContent = await page.getTextContent();
    page.cleanup();
    return {
      pageIndex,
      canvas,
      width: canvas.width,
      height: canvas.height,
      viewport,
      textContent,
    };
  }

  private evictIfFull(): void {
    while (this.entries.size >= this.capacity) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      const entry = this.entries.get(oldest);
      if (entry) {
        entry.canvas.width = 0;
        entry.canvas.height = 0;
      }
      this.entries.delete(oldest);
    }
  }
}
